import moment from 'moment'
import exploreService from '@/api/exploreService'
import { schemeAccent } from 'd3'
import i18n from '@/i18n'

export default {
  name: 'ExploreAnalysis',
  mixins: [],
  components: {
    ExploreWidget: () => import(/* webpackChunkName: "ExploreWidget" */'@/views/explore/explore-widget/ExploreWidget.vue')
  },
  props: {
    model: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        name: null,
        type: 'postalcode',
        geometries: [],
        startDate: new Date(),
        finishDate: new Date()
      })
    }
  },
  data () {
    return {
      showForm: false,
      newModelName: null,
      formLoading: false,
      scrollpointReached: false,
      selectedTab: 'mobileaudience',
      widgets: {
        mobileaudience: [
          {
            key: 'unique_users',
            title: i18n.t('DAILY_UNIQUE_USERS'),
            data: null,
            loading: true,
            type: 'single',
            hasCount: true
          },
          {
            key: 'demography',
            title: i18n.t('DAILY_DEMOGRAPHY'),
            data: null,
            loading: true,
            type: 'categories-multiple'
          },
          // {
          //   key: 'total_reached_population',
          //   title: 'Alcance total de habitantes',
          //   data: null,
          //   loading: true,
          //   type: 'categories'
          // },
          // {
          //   key: 'zone_affinity_index',
          //   title: 'Índice de afinidad de Zona',
          //   data: null,
          //   loading: true,
          //   type: 'categories'
          // },
          {
            key: 'interests',
            title: i18n.t('INTERESTS'),
            data: null,
            loading: true,
            type: 'categories'
          }
        ],
        economy: [
          {
            key: 'pois',
            title: i18n.t('POIS'),
            data: null,
            loading: true,
            type: 'categories'
          }
        ],
        census: [
          {
            key: 'census_demography',
            title: i18n.t('DEMOGRAPHY'),
            data: null,
            loading: true,
            type: 'categories-multiple',
            labels: {
              female: i18n.t('WOMEN'),
              male: i18n.t('MEN')
            }
          },
          {
            key: 'marital_status',
            title: i18n.t('MARITAL_STATUS'),
            data: null,
            loading: true,
            type: 'categories-multiple',
            labels: {
              single: i18n.t('SINGLE'),
              married: i18n.t('MARRIED'),
              divorced: i18n.t('DIVORCED'),
              separated: i18n.t('SEPARATED'),
              widowed: i18n.t('WIDOWED')
            },
            colors: {
              single: schemeAccent[0],
              married: schemeAccent[1],
              divorced: schemeAccent[2],
              separated: schemeAccent[3],
              widowed: schemeAccent[4]
            }
          },
          {
            key: 'education_level',
            title: i18n.t('EDUCATION_LEVEL'),
            data: null,
            loading: true,
            type: 'categories'
          },
          {
            key: 'house_holds',
            title: i18n.t('HOUSEHOLDS'),
            data: null,
            loading: true,
            type: 'categories'
          }
        ]
      }
    }
  },
  computed: {
    typeLabel () {
      const labels = {
        grid: 'GRID',
        area: 'AREA',
        postalcode: 'ZIPCODE'
      }
      return i18n.tc(labels[this.model.type], this.model.geometries.length)
    },
    startDate () {
      return this.model.startDate.toLocaleDateString()
    },
    finishDate () {
      return this.model.finishDate.toLocaleDateString()
    },
    dateDiif () {
      return moment(this.model.finishDate).diff(this.model.startDate, 'days') + 1
    },
    modelLabel () {
      return this.model.id
        ? this.model.name
        : `${this.model.geometries.length} ${this.typeLabel}`
    }
  },
  async mounted () {
    if (this.model.type === 'area') {
      await this.createArea()
    }
    const body = {
      zone_type: this.model.type,
      start_date: this.model.startDate.toJSON().split('T')[0],
      finish_date: this.model.finishDate.toJSON().split('T')[0],
      zones: this.model.geometries.map(g => g.id)
    }
    const mapping = {
      mobileaudience: 'getMobileAudience',
      economy: 'getEconomy',
      census: 'getCensus'
    }
    for (const key in mapping) {
      const method = mapping[key]
      this.widgets[key].forEach(w => {
        exploreService[method](w.key, body).then(data => {
          w.loading = false
          if (JSON.stringify(data) === '{}') {
            return
          }
          if (w.key === 'demography') {
            let count = 0
            for (const group of data) {
              group.category = group.category.replace('>=', '≥').replace('<=', '≤')
              for (const val of group.value) {
                count += val.value
              }
            }
            data = {
              count,
              results: data
            }
          }
          if (w.key === 'census_demography') {
            let count = 0
            for (const group of data) {
              group.category = group.category.replace('>=', '≥').replace('<=', '≤')
              for (const val of group.value) {
                count += val.value
              }
            }
            data = {
              count,
              results: data
            }
          }
          if (w.key === 'zone_affinity_index') {
            data = data.results.map(d => ({ category: d.category, value: d.index_value }))
          }
          if (w.key === 'pois') {
            data = Array.isArray(data)
              ? data.map(d => ({ category: d.name, value: d.value }))
              : []
          }
          if (w.key === 'interests') {
            data = Array.isArray(data)
              ? data.map(d => ({ category: d.category_name, value: d.value }))
              : []
          }
          if (w.key === 'unique_users') {
            data = { count: data.value }
          }
          if (w.key === 'marital_status') {
            data = { results: data }
          }
          if (w.type === 'categories') {
            data.sort((a, b) => b.value - a.value)
          }
          w.data = data
        }).catch(err => {
          // eslint-disable-next-line
          console.error(`[explore-analysis] Error fetching widget ${w.widget}`, err)
          w.loading = false
        })
      })
    }
  },
  methods: {
    async saveModel () {
      this.formLoading = true
      try {
        const model = await exploreService.saveGeoTemplate({
          id: this.model.id,
          name: this.newModelName,
          type: 'postalcode',
          geometries: this.model.geometries
        })
        this.model.id = model.id
        this.model.name = model.name
        this.closeForm()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[explore-analysis.js] Error saving explore zones', err)
      }
      this.formLoading = false
    },
    async deleteModel () {
      this.formLoading = true
      try {
        await exploreService.deleteZipcodeGroup(this.model.id)
        this.goBack()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[explore-analysis.js] Error deleting zipcode group', err)
      }
      this.formLoading = false
    },
    goBack () {
      this.$emit('toggleAnalysis')
    },
    openForm () {
      this.showForm = true
      if (this.model.id && this.model.name) {
        this.newModelName = this.model.name
      }
      this.$nextTick(() => {
        if (this.$refs.nameinput) {
          this.$refs.nameinput.onFocus()
        }
      })
    },
    closeForm () {
      this.showForm = false
    },
    onTabSelected ({ id }) {
      this.selectedTab = id
    },
    onScroll ({ y }) {
      this.scrollpointReached = y > 164
    },
    async createArea () {
      const area = await exploreService.createArea(this.model.geometries[0])
      this.model.geometries[0].id = area.id
    }
  }
}
